const SLOT_INTERVAL_MINUTES = 30;
const DAY_START_MINUTES = 7 * 60;
const DAY_END_MINUTES = 22 * 60 + 30;
const SHIFT_STATUS_DAY_OFF = 'DAY OFF';
const SHIFT_STATUS_LEAVE = 'LEAVE';
const SHIFT_STATUS_HOLIDAY = 'HOLIDAY';

export const ROSTER_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const ROSTER_SLOT_MINUTES = Array.from(
  { length: (DAY_END_MINUTES - DAY_START_MINUTES) / SLOT_INTERVAL_MINUTES },
  (_, index) => DAY_START_MINUTES + index * SLOT_INTERVAL_MINUTES
);
export const DEFAULT_TASK_OPTIONS = [
  'Picking',
  'Compliance',
  'Billing',
  'Cleaning',
  'Inventory',
  'Receiving',
];
export const DEFAULT_SHIFT_OPTIONS = [
  '07:00-12:30',
  '07:00-15:00',
  '10:00-18:00',
  '12:00-17:30',
  '14:30-22:30',
  '15:00-22:30',
  SHIFT_STATUS_DAY_OFF,
  SHIFT_STATUS_LEAVE,
  SHIFT_STATUS_HOLIDAY,
];
export const COMMON_SHIFT_SUGGESTIONS = [...DEFAULT_SHIFT_OPTIONS];

const normalizeText = (value) => String(value ?? '').trim();

const slugify = (value) => (
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
);

const uniqueValues = (values = []) => Array.from(new Set(values.filter(Boolean).map((value) => normalizeText(value)).filter(Boolean)));

const ensureUniqueId = (baseId, existingIds = []) => {
  const idSet = new Set(existingIds);
  if (!idSet.has(baseId)) return baseId;

  let counter = 2;
  while (idSet.has(`${baseId}-${counter}`)) {
    counter += 1;
  }

  return `${baseId}-${counter}`;
};

export const formatRosterTime = (minutes) => {
  const safeMinutes = Number(minutes);
  if (!Number.isFinite(safeMinutes) || safeMinutes < 0) return '';
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

export const parseRosterTime = (value) => {
  const match = normalizeText(value).match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return Number.NaN;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return Number.NaN;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return Number.NaN;
  return hours * 60 + minutes;
};

export const buildTaskSlotKey = (slotMinutes) => formatRosterTime(slotMinutes);

export const getShiftStatus = (shift) => {
  const compact = normalizeText(shift).toUpperCase().replace(/\s+/g, '');
  if (!compact) return '';
  if (compact === 'DAYOFF' || compact === 'OFF') return SHIFT_STATUS_DAY_OFF;
  if (compact === 'LEAVE') return SHIFT_STATUS_LEAVE;
  if (compact === 'HOLIDAY') return SHIFT_STATUS_HOLIDAY;
  return '';
};

export const isDayOffShift = (shift) => getShiftStatus(shift) === SHIFT_STATUS_DAY_OFF;

const parseFlexibleTime = (value) => {
  const token = normalizeText(value)
    .replace(/[.]/g, ':')
    .replace(/\s+/g, ' ')
    .trim();
  if (!token) return Number.NaN;

  const match = token.match(/^(\d{1,2})(?::(\d{1,2}))?\s*([AP]M)?$/i);
  if (!match) return Number.NaN;

  let hours = Number(match[1]);
  let minutes = Number(match[2] ?? '0');
  const meridian = String(match[3] || '').toUpperCase();

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return Number.NaN;
  if (minutes < 0 || minutes > 59) return Number.NaN;

  if (meridian) {
    if (hours < 1 || hours > 12) return Number.NaN;
    if (meridian === 'AM') {
      if (hours === 12) hours = 0;
    } else if (meridian === 'PM') {
      if (hours !== 12) hours += 12;
    }
  }

  if (hours < 0 || hours > 23) return Number.NaN;
  return hours * 60 + minutes;
};

export const normalizeShiftInput = (value) => {
  const trimmed = normalizeText(value).toUpperCase();
  if (!trimmed) return '';
  const shiftStatus = getShiftStatus(trimmed);
  if (shiftStatus) return shiftStatus;

  const normalizedDelimiters = normalizeText(value).replace(/[\u2010-\u2015\u2212]/g, '-');
  const parts = normalizedDelimiters.split(/\s*(?:-|to)\s*/i).filter(Boolean);
  if (parts.length !== 2) return '';

  const start = parseFlexibleTime(parts[0]);
  const end = parseFlexibleTime(parts[1]);

  if (!Number.isFinite(start) || !Number.isFinite(end)) return '';
  if (start >= end) return '';

  return `${formatRosterTime(start)}-${formatRosterTime(end)}`;
};

export const parseShift = (shift) => {
  const normalized = normalizeShiftInput(shift);
  if (!normalized || getShiftStatus(normalized)) return null;

  const [startValue, endValue] = normalized.split('-');
  const start = parseRosterTime(startValue);
  const end = parseRosterTime(endValue);
  if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) return null;

  return {
    start,
    end,
    durationMinutes: end - start,
    label: normalized,
  };
};

export const getBreakMinutes = (shift) => {
  const parsed = parseShift(shift);
  if (!parsed) return 0;
  if (parsed.durationMinutes <= SLOT_INTERVAL_MINUTES) return 0;
  return parsed.durationMinutes <= 6 * 60 ? 30 : 60;
};

export const getBreakWindow = (shift) => {
  const parsed = parseShift(shift);
  const breakMinutes = getBreakMinutes(shift);
  if (!parsed || breakMinutes <= 0) return null;

  const desiredStart = parsed.start + (parsed.durationMinutes - breakMinutes) / 2;
  const roundedStart = Math.round(desiredStart / SLOT_INTERVAL_MINUTES) * SLOT_INTERVAL_MINUTES;
  const clampedStart = Math.max(parsed.start, Math.min(parsed.end - breakMinutes, roundedStart));

  return {
    start: clampedStart,
    end: clampedStart + breakMinutes,
    minutes: breakMinutes,
  };
};

export const getPaidMinutes = (shift) => {
  const parsed = parseShift(shift);
  if (!parsed) return 0;
  return Math.max(0, parsed.durationMinutes);
};

export const formatHourValue = (minutes) => {
  const hours = minutes / 60;
  return `${hours % 1 === 0 ? hours.toFixed(0) : hours.toFixed(1)}h`;
};

export const getShiftSlotState = (shift, slotMinutes) => {
  const shiftStatus = getShiftStatus(shift);
  if (shiftStatus === SHIFT_STATUS_DAY_OFF) return 'day-off';
  if (shiftStatus === SHIFT_STATUS_LEAVE) return 'leave';
  if (shiftStatus === SHIFT_STATUS_HOLIDAY) return 'holiday';

  const parsed = parseShift(shift);
  if (!parsed) return 'inactive';
  if (slotMinutes < parsed.start || slotMinutes >= parsed.end) return 'inactive';

  return 'work';
};

export const pruneTaskAssignmentsForShift = (taskEntries = {}, shift) => {
  const entries = Object.entries(taskEntries || {});

  return entries.reduce((accumulator, [slotKey, taskValue]) => {
    const slotMinutes = parseRosterTime(slotKey);
    if (!Number.isFinite(slotMinutes)) return accumulator;
    if (getShiftSlotState(shift, slotMinutes) !== 'work') return accumulator;

    const normalizedTask = normalizeText(taskValue);
    if (!normalizedTask) return accumulator;

    accumulator[slotKey] = normalizedTask;
    return accumulator;
  }, {});
};

export const calculateWeeklyMinutes = (roster = {}, staffId = '') => (
  ROSTER_DAYS.reduce((total, day) => total + getPaidMinutes(roster?.[day]?.[staffId]), 0)
);

export const calculateDailyCoverageMinutes = (roster = {}, staff = [], day) => (
  (staff || []).reduce((total, member) => total + getPaidMinutes(roster?.[day]?.[member.id]), 0)
);

export const createStaffMember = (name, existingStaff = []) => {
  const trimmedName = normalizeText(name);
  const baseId = slugify(trimmedName) || 'team-member';
  const existingIds = existingStaff.map((member) => member.id);

  return {
    id: ensureUniqueId(baseId, existingIds),
    name: trimmedName || 'New Team Member',
  };
};

const normalizeStaff = (staff = []) => {
  const seen = new Set();

  return (Array.isArray(staff) ? staff : [])
    .map((member) => ({
      id: normalizeText(member?.id) || slugify(member?.name) || '',
      name: normalizeText(member?.name),
    }))
    .filter((member) => member.id && member.name)
    .map((member) => {
      const nextId = ensureUniqueId(member.id, Array.from(seen));
      seen.add(nextId);
      return { ...member, id: nextId };
    });
};

export const normalizeWorkforceRoster = (value = {}) => {
  const staff = normalizeStaff(value.staff);
  const safeStaff = staff;

  const roster = ROSTER_DAYS.reduce((dayAccumulator, day) => {
    const rawDayRoster = value?.roster?.[day] || {};
    dayAccumulator[day] = safeStaff.reduce((memberAccumulator, member) => {
      const rawShift = rawDayRoster?.[member.id];
      memberAccumulator[member.id] = normalizeShiftInput(rawShift) || '';
      return memberAccumulator;
    }, {});
    return dayAccumulator;
  }, {});

  const tasks = ROSTER_DAYS.reduce((dayAccumulator, day) => {
    const rawDayTasks = value?.tasks?.[day] || {};
    const normalizedDayTasks = safeStaff.reduce((memberAccumulator, member) => {
      const nextTasks = pruneTaskAssignmentsForShift(rawDayTasks?.[member.id], roster?.[day]?.[member.id]);
      if (Object.keys(nextTasks).length) {
        memberAccumulator[member.id] = nextTasks;
      }
      return memberAccumulator;
    }, {});

    dayAccumulator[day] = normalizedDayTasks;
    return dayAccumulator;
  }, {});

  const assignedTaskLabels = uniqueValues(
    ROSTER_DAYS.flatMap((day) =>
      safeStaff.flatMap((member) => Object.values(tasks?.[day]?.[member.id] || {}))
    )
  );
  const taskOptions = uniqueValues([...(value?.taskOptions || []), ...DEFAULT_TASK_OPTIONS, ...assignedTaskLabels]);
  const assignedShiftValues = uniqueValues(
    ROSTER_DAYS.flatMap((day) => safeStaff.map((member) => roster?.[day]?.[member.id]))
  );
  const shiftOptions = uniqueValues([...(value?.shiftOptions || []), ...COMMON_SHIFT_SUGGESTIONS, ...assignedShiftValues]);

  return {
    staff: safeStaff,
    roster,
    tasks,
    taskOptions,
    shiftOptions,
  };
};

export { DAY_END_MINUTES, DAY_START_MINUTES };
